import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { useInfiniteList } from "@/hooks/useInfiniteList";
import { initials, statusMeta } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useTableSort, SortTh, useColumns, ColumnPicker, ExportCsvButton } from "@/components/SortableHeader";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Search, UserCheck, Loader2, Pencil, Trash2 } from "lucide-react";
import { GuestInviteDialog } from "@/components/GuestInviteDialog";

export default function Guests() {
  const { t } = useLang();
  const { can } = useAuth();
  const manage = can("guests.manage");
  const canInvite = manage || can("guests.invite");
  const [guests, setGuests] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const sort = useTableSort("displayName", "asc", "guests");
  const cols = useColumns("guests");
  const [loading, setLoading] = useState(true);
  const [guestOpen, setGuestOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users/guests").then((r) => setGuests(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openEdit = (u) => { setEditing(u); setEditName(u.displayName || ""); };
  const saveEdit = async () => {
    if (!editName.trim()) { toast.error(t("guest.needName")); return; }
    setBusy(true);
    try { await api.patch(`/guests/${editing.id}`, { displayName: editName.trim() }); toast.success(t("guest.updated")); setEditing(null); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const confirmDelete = async () => {
    setBusy(true);
    try { await api.delete(`/guests/${deleting.id}`); toast.success(t("guest.deleted")); setDeleting(null); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const guestStatus = (u) => u.status || (u.accountEnabled ? "active" : "disabled");
  const filtered = useMemo(() => {
    const rows = guests
      .filter((u) => `${u.displayName} ${u.userPrincipalName} ${u.mail || ""}`.toLowerCase().includes(q.toLowerCase()))
      .filter((u) => statusFilter === "all" || guestStatus(u) === statusFilter);
    return sort.sortRows(rows, { status: guestStatus });
  }, [guests, q, statusFilter, sort]);
  const csvHeaders = [
    { key: "displayName", label: t("guests.colGuest") },
    { key: "userPrincipalName", label: "UPN" },
    ...(cols.show("mail") ? [{ key: "mail", label: t("guests.colEmail") }] : []),
    { key: "status", label: t("common.status") },
  ];
  const csvRows = filtered.map((u) => ({ displayName: u.displayName, userPrincipalName: u.userPrincipalName, mail: u.mail || "", status: guestStatus(u) }));
  const { visible, sentinelRef, hasMore, shown, total } = useInfiniteList(filtered, 50);

  return (
    <>
      <PageHeader title={t("guests.title")} subtitle={`${guests.length} ${t("guests.title").toLowerCase()}`}>
        {canInvite && (
          <button onClick={() => setGuestOpen(true)} data-testid="guests-invite-guest-button" className="flex h-10 items-center gap-2 bg-primary px-4 text-sm font-semibold uppercase tracking-wide text-primary-foreground transition-colors hover:bg-[#002480]">
            <UserCheck size={16} /> {t("guest.invite")}
          </button>
        )}
      </PageHeader>
      <div className="p-6 md:p-10">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("guests.searchPh")} data-testid="guests-search" className="rounded-sm border-zinc-300 pl-9 focus-visible:ring-2 focus-visible:ring-primary" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ColumnPicker label={t("cols.columns")} cols={cols} columns={[{ key: "mail", label: t("guests.colEmail") }]} />
            <ExportCsvButton label={t("cols.export")} filename="guests.csv" headers={csvHeaders} rows={csvRows} />
          </div>
          {!manage && (
            <div className="flex items-center gap-2 border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-700">
              <UserCheck size={14} /> {t("guests.readOnly")}
            </div>
          )}
        </div>

        <div className="hidden border border-zinc-200 bg-white md:block">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                <SortTh label={t("guests.colGuest")} sortKey="displayName" sort={sort} />
                {cols.show("mail") && <SortTh label={t("guests.colEmail")} sortKey="mail" sort={sort} className="hidden md:table-cell" />}
                <SortTh label={t("common.status")} sortKey="status" sort={sort} filter={{ value: statusFilter, onChange: setStatusFilter, allValue: "all", options: [
                  { value: "all", label: t("filter.allStatuses") },
                  { value: "active", label: statusMeta("active").label },
                  { value: "disabled", label: statusMeta("disabled").label },
                ] }} />
                {manage && <th className="px-4 py-3 text-right">{t("teams.colActions")}</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={4} className="px-4 py-10 text-center font-mono text-xs uppercase tracking-widest text-zinc-400">{t("common.loading")}</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-zinc-400">{t("guests.empty")}</td></tr>}
              {visible.map((u) => {
                const st = statusMeta(u.status || (u.accountEnabled ? "active" : "disabled"));
                return (
                  <tr key={u.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`guest-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-violet-700 text-xs font-bold text-white">{initials(u.displayName)}</span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-zinc-900">{u.displayName}</span>
                          <span className="block truncate font-mono text-xs text-zinc-400">{u.userPrincipalName}</span>
                        </span>
                      </div>
                    </td>
                    {cols.show("mail") && <td className="hidden px-4 py-3 text-sm text-zinc-600 md:table-cell">{u.mail || "—"}</td>}
                    <td className="px-4 py-3"><span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${st.cls}`}>{st.label}</span></td>
                    {manage && (
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openEdit(u)} data-testid={`guest-edit-${u.id}`} className="p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"><Pencil size={15} /></button>
                          <button onClick={() => setDeleting(u)} data-testid={`guest-delete-${u.id}`} className="p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="space-y-3 md:hidden" data-testid="guests-mobile-list">
          {loading && <div className="border border-zinc-200 bg-white px-4 py-10 text-center font-mono text-xs uppercase tracking-widest text-zinc-400">{t("common.loading")}</div>}
          {!loading && filtered.length === 0 && <div className="border border-zinc-200 bg-white px-4 py-10 text-center text-sm text-zinc-400">{t("guests.empty")}</div>}
          {visible.map((u) => {
            const st = statusMeta(u.status || (u.accountEnabled ? "active" : "disabled"));
            return (
              <div key={u.id} className="border border-zinc-200 bg-white p-4" data-testid={`guest-card-${u.id}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-violet-700 text-sm font-bold text-white">{initials(u.displayName)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-zinc-900">{u.displayName}</div>
                    <div className="truncate font-mono text-[11px] text-zinc-400">{u.userPrincipalName}</div>
                  </div>
                  <span className={`inline-block shrink-0 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${st.cls}`}>{st.label}</span>
                </div>
                {u.mail && <div className="mt-2 truncate text-xs text-zinc-500">{u.mail}</div>}
                {manage && (
                  <div className="mt-3 flex items-center gap-4 border-t border-zinc-100 pt-3">
                    <button onClick={() => openEdit(u)} data-testid={`m-guest-edit-${u.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-600 hover:text-zinc-900"><Pencil size={14} /> {t("guest.editTitle")}</button>
                    <button onClick={() => setDeleting(u)} data-testid={`m-guest-delete-${u.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline"><Trash2 size={14} /> {t("teams.delete")}</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {hasMore && <div ref={sentinelRef} className="flex items-center justify-center py-4 text-xs text-zinc-400"><Loader2 size={14} className="mr-2 animate-spin" /> {shown}/{total}</div>}
      </div>

      <GuestInviteDialog open={guestOpen} onOpenChange={setGuestOpen} onInvited={load} />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">{t("guest.editTitle")}</DialogTitle>
            <DialogDescription>{t("guest.displayName")}</DialogDescription></DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("guest.displayName")}</Label>
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="guest-edit-name" className="rounded-sm border-zinc-300" />
            <p className="font-mono text-[11px] text-zinc-400">{editing?.userPrincipalName}</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="guest-edit-save" className="rounded-sm bg-primary hover:bg-[#002480]">{busy ? <Loader2 size={15} className="animate-spin" /> : t("teams.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("guest.deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>{t("guest.deleteWarn")} <span className="font-semibold text-zinc-700">{deleting?.displayName}</span> ({deleting?.mail})</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="guest-delete-confirm" className="rounded-sm bg-red-600 hover:bg-red-700">{t("teams.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
